import { ERROR_MAINTENANCE, ERRORS_MAP } from './constants';

/**
 * Response validation and error handling utility
 * Provides a single source of truth for handling API responses across the application
 */

/**
 * Response status enum for standardized handling
 */
export const RESPONSE_STATUS = {
    SUCCESS: 'SUCCESS',
    MAINTENANCE: 'MAINTENANCE',
    SHOP_ACCESS_DENIED: 'SHOP_ACCESS_DENIED',
    EMPTY_RESPONSE: 'EMPTY_RESPONSE',
};

/**
 * Validates and categorizes API response
 * 
 * @param {Object} response - API response object
 * @returns {Object} - { status, reason, data }
 */
export const validateResponse = (response, options = {}) => {
    const { requiresShopAccess = true } = options;

    // Check for empty response
    if (!response || response.empty) {
        return {
            status: RESPONSE_STATUS.EMPTY_RESPONSE,
            reason: null,
            data: response,
        };
    }

    // Some endpoints answer with their own bare payload and no CanShop flag
    // (e.g. claimMilestoneReward -> {Success: bool}). Gating those on CanShop
    // classifies every response as SHOP_ACCESS_DENIED, so onSuccess never runs.
    // Such callers pass requiresShopAccess: false.
    if (!requiresShopAccess) {
        return {
            status: RESPONSE_STATUS.SUCCESS,
            reason: null,
            data: response,
        };
    }

    // Check for maintenance mode
    if (!response.CanShop && response.Reason === ERROR_MAINTENANCE) {
        return {
            status: RESPONSE_STATUS.MAINTENANCE,
            reason: ERROR_MAINTENANCE,
            data: response,
        };
    }

    // Check for shop access denied
    if (!response.CanShop) {
        return {
            status: RESPONSE_STATUS.SHOP_ACCESS_DENIED,
            reason: response.Reason,
            data: response,
        };
    }

    // Successful response
    if (response.CanShop) {
        return {
            status: RESPONSE_STATUS.SUCCESS,
            reason: null,
            data: response,
        };
    }

    // Fallback for unexpected response structure
    return {
        status: RESPONSE_STATUS.EMPTY_RESPONSE,
        reason: null,
        data: response,
    };
};

/**
 * Handles response based on validation result with navigation and callbacks
 * 
 * @param {Object} validationResult - Result from validateResponse
 * @param {Object} callbacks - Callback functions for different scenarios
 * @param {Function} callbacks.onSuccess - Called when response is successful
 * @param {Function} callbacks.onMaintenance - Called when in maintenance mode
 * @param {Function} callbacks.onAccessDenied - Called when shop access is denied
 * @param {Function} callbacks.onEmpty - Called when response is empty
 * @param {Object} navigate - React Router navigate function
 * @returns {boolean} - Returns true if response was handled successfully
 */
export const handleResponse = (validationResult, callbacks = {}, navigate = null) => {
    const { status, reason, data } = validationResult;

    switch (status) {
        case RESPONSE_STATUS.SUCCESS:
            if (callbacks.onSuccess) {
                callbacks.onSuccess(data);
                return true;
            }
            return true;

        case RESPONSE_STATUS.MAINTENANCE:
            if (navigate) {
                navigate('/maintenance');
            }
            if (callbacks.onMaintenance) {
                callbacks.onMaintenance();
            }
            return false;

        case RESPONSE_STATUS.SHOP_ACCESS_DENIED:
            if (callbacks.onAccessDenied) {
                callbacks.onAccessDenied(reason, data);
            }
            return false;

        case RESPONSE_STATUS.EMPTY_RESPONSE:
            if (callbacks.onEmpty) {
                callbacks.onEmpty(data);
            }
            return false;

        default:
            return false;
    }
};

/**
 * Higher-order function to wrap API calls with standard response handling
 * 
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} options - Configuration options
 * @param {Function} options.navigate - React Router navigate function
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onAccessDenied - Access denied callback
 * @param {Function} options.onMaintenance - Maintenance callback
 * @param {Function} options.onEmpty - Empty response callback
 * @param {Function} options.onError - Error callback for exceptions
 * @param {boolean} [options.requiresShopAccess=true] - Set false for endpoints
 *        that return their own payload without a CanShop flag
 * @returns {Promise<Object>} - Returns validation result
 */
export const withResponseHandling = async (apiCall, options = {}) => {
    try {
        const response = await apiCall();
        const validationResult = validateResponse(response, {
            requiresShopAccess: options.requiresShopAccess,
        });
        
        handleResponse(validationResult, {
            onSuccess: options.onSuccess,
            onMaintenance: options.onMaintenance,
            onAccessDenied: options.onAccessDenied,
            onEmpty: options.onEmpty,
        }, options.navigate);

        return validationResult;
    } catch (error) {
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};

/**
 * Get user-friendly error message from response reason
 * 
 * @param {number} reason - Response reason code
 * @param {Object} config - Config object with error messages
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (reason, config) => {
    if (reason !== null && reason !== undefined && ERRORS_MAP[reason]) {
        return config.errors[ERRORS_MAP[reason]] || config.errors.WENT_WRONG;
    }
    return config.errors.WENT_WRONG;
};

/**
 * Standard logout handler
 * Clears all local storage and resets state
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.navigate - React Router navigate function
 * @param {Function} options.setIsLogged - State setter for logged status
 * @param {Function} options.setHasUnclaimedMilestones - State setter for milestones
 */
export const performLogout = (options = {}) => {
    localStorage.removeItem('playerInfo');
    localStorage.removeItem('selectedOffer');
    localStorage.removeItem('adyen-checkout__session');
    localStorage.removeItem('filters');
    
    if (options.setIsLogged) {
        options.setIsLogged(false);
    }
    
    if (options.setHasUnclaimedMilestones) {
        options.setHasUnclaimedMilestones(false);
    }
    
    if (options.navigate) {
        options.navigate('/');
    }
};
